import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Eye, EyeSlash, GoogleLogo, User, Baby, FileText, Basketball, CheckCircle, ArrowRight, ArrowLeft } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';

import { getApiBaseUrl } from '../utils/api';

const STEPS = [
  { id: 1, title: 'Your Account', icon: User },
  { id: 2, title: 'Add Player', icon: Baby },
  { id: 3, title: 'Sign Waiver', icon: FileText },
  { id: 4, title: 'Find Session', icon: Basketball },
];

export function Onboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Step 1: Account data
  const [accountData, setAccountData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
  });
  
  // Step 2: Player data
  const [playerData, setPlayerData] = useState({
    name: '',
    date_of_birth: '',
    grade: '',
    instagram_handle: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
  });
  const [createdPlayer, setCreatedPlayer] = useState(null);
  
  // Step 3: Waiver data
  const [waiverTemplate, setWaiverTemplate] = useState(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isParentGuardian, setIsParentGuardian] = useState(false);
  const [consentDataCollection, setConsentDataCollection] = useState(false);
  const [consentTermsPrivacy, setConsentTermsPrivacy] = useState(false);
  const [consentPhotoVideo, setConsentPhotoVideo] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [waiverStep, setWaiverStep] = useState(1); // 1: Read, 2: Consent, 3: Sign
  const waiverScrollRef = useRef(null);
  
  // Load waiver template on mount
  useEffect(() => {
    loadWaiverTemplate();
  }, []);
  
  const loadWaiverTemplate = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/waivers/template`);
      if (response.ok) {
        const data = await response.json();
        setWaiverTemplate(data);
      }
    } catch (error) {
      console.error('Failed to load waiver template:', error);
    }
  };
  
  const handleAccountChange = (e) => {
    setAccountData({ ...accountData, [e.target.name]: e.target.value });
  };
  
  const handlePlayerChange = (e) => {
    setPlayerData({ ...playerData, [e.target.name]: e.target.value });
  };
  
  const handleGradeChange = (value) => {
    setPlayerData({ ...playerData, grade: value });
  };
  
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      setHasScrolledToBottom(true);
    }
  }, []);
  
  // Step 1: Create Account
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...accountData, role: 'parent' })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }
      
      toast.success('Account created!');
      setCurrentStep(2);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Google OAuth
  const handleGoogleLogin = () => {
    // Store that we're in onboarding flow
    sessionStorage.setItem('onboarding_flow', 'true');
    const redirectUrl = window.location.origin + '/onboarding?step=2';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  // Check if returning from Google OAuth
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stepParam = urlParams.get('step');
    if (stepParam === '2' && sessionStorage.getItem('onboarding_flow')) {
      setCurrentStep(2);
      sessionStorage.removeItem('onboarding_flow');
    }
  }, []);
  
  // Step 2: Create Player
  const handleCreatePlayer = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/players`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...playerData,
          grade: playerData.grade ? parseInt(playerData.grade) : null,
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to add player');
      }
      
      const player = await response.json();
      setCreatedPlayer(player);
      toast.success(`${player.name} added!`);
      setCurrentStep(3);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Step 3: Sign Waiver
  const proceedToConsent = () => {
    if (!hasScrolledToBottom) {
      toast.error('Please scroll to read the entire waiver');
      return;
    }
    setWaiverStep(2);
  };
  
  const proceedToSign = () => {
    if (!isParentGuardian || !consentDataCollection || !consentTermsPrivacy || !consentPhotoVideo) {
      toast.error('Please check all required consent boxes');
      return;
    }
    setWaiverStep(3);
  };
  
  const handleSignWaiver = async () => {
    if (!signedName.trim() || signedName.trim().length < 2) {
      toast.error('Please enter your full legal name');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/waivers/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          player_id: createdPlayer.player_id,
          signed_name: signedName.trim(),
          is_parent_guardian: isParentGuardian,
          consent_data_collection: consentDataCollection,
          consent_terms_privacy: consentTermsPrivacy,
          consent_photo_video: consentPhotoVideo
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to sign waiver');
      }
      
      toast.success('Waiver signed successfully!');
      setCurrentStep(4);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Step 4: Go to sessions
  const goToSessions = () => {
    navigate('/sessions');
  };
  
  // Progress indicator
  const renderProgress = () => (
    <div className="flex items-center justify-center mb-8">
      {STEPS.map((step, index) => {
        const Icon = step.icon;
        const isActive = currentStep === step.id;
        const isComplete = currentStep > step.id;
        
        return (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                isComplete ? 'bg-green-500' : isActive ? 'bg-primary' : 'bg-muted'
              }`}>
                {isComplete ? (
                  <CheckCircle size={20} weight="fill" className="text-white" />
                ) : (
                  <Icon size={20} className={isActive ? 'text-white' : 'text-muted-foreground'} />
                )}
              </div>
              <span className={`text-xs mt-1 hidden sm:block ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                {step.title}
              </span>
            </div>
            {index < STEPS.length - 1 && (
              <div className={`w-8 sm:w-16 h-0.5 mx-1 sm:mx-2 ${
                currentStep > step.id ? 'bg-green-500' : 'bg-muted'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
  
  // Step 1: Account Form
  const renderAccountStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-heading text-white mb-2">Create Your Account</h2>
        <p className="text-muted-foreground">Parent/Guardian information</p>
      </div>
      
      <Button
        onClick={handleGoogleLogin}
        variant="outline"
        className="w-full border-primary/50 hover:border-primary hover:bg-primary/10"
        data-testid="google-signup-btn"
      >
        <GoogleLogo size={20} weight="bold" className="mr-2" />
        Continue with Google
      </Button>
      
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">Or with email</span>
        </div>
      </div>
      
      <form onSubmit={handleCreateAccount} className="space-y-4">
        <div>
          <Label htmlFor="name">Full Name *</Label>
          <Input
            id="name"
            name="name"
            value={accountData.name}
            onChange={handleAccountChange}
            required
            placeholder="Your full name"
            className="mt-1"
            data-testid="account-name-input"
          />
        </div>
        
        <div>
          <Label htmlFor="email">Email Address *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={accountData.email}
            onChange={handleAccountChange}
            required
            placeholder="your@email.com"
            className="mt-1"
            data-testid="account-email-input"
          />
        </div>
        
        <div>
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={accountData.phone}
            onChange={handleAccountChange}
            placeholder="(555) 123-4567"
            className="mt-1"
            data-testid="account-phone-input"
          />
        </div>
        
        <div>
          <Label htmlFor="password">Password *</Label>
          <div className="relative mt-1">
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={accountData.password}
              onChange={handleAccountChange}
              required
              minLength={6}
              placeholder="Min 6 characters"
              className="pr-10"
              data-testid="account-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
            >
              {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>
        
        <Button type="submit" disabled={loading} className="w-full btn-primary" data-testid="create-account-btn">
          {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
          Continue
          <ArrowRight size={16} className="ml-2" />
        </Button>
      </form>
      
      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <Link to="/login" className="text-primary hover:underline">Sign in</Link>
      </p>
    </div>
  );
  
  // Step 2: Player Form
  const renderPlayerStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-heading text-white mb-2">Add Your Player</h2>
        <p className="text-muted-foreground">Enter your child's information</p>
      </div>
      
      <form onSubmit={handleCreatePlayer} className="space-y-4">
        <div>
          <Label htmlFor="playerName">Player's Full Name *</Label>
          <Input
            id="playerName"
            name="name"
            value={playerData.name}
            onChange={handlePlayerChange}
            required
            placeholder="Child's full name"
            className="mt-1"
            data-testid="player-name-input"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="dob">Date of Birth *</Label>
            <Input
              id="dob"
              name="date_of_birth"
              type="date"
              value={playerData.date_of_birth}
              onChange={handlePlayerChange}
              required
              className="mt-1"
              data-testid="player-dob-input"
            />
          </div>
          
          <div>
            <Label htmlFor="grade">Grade Level *</Label>
            <Select value={playerData.grade} onValueChange={handleGradeChange}>
              <SelectTrigger className="mt-1" data-testid="player-grade-select">
                <SelectValue placeholder="Select grade" />
              </SelectTrigger>
              <SelectContent>
                {[3,4,5,6,7,8,9,10,11,12].map(g => (
                  <SelectItem key={g} value={g.toString()}>
                    {g === 1 ? '1st' : g === 2 ? '2nd' : g === 3 ? '3rd' : `${g}th`} Grade
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div>
          <Label htmlFor="instagram">Instagram Handle *</Label>
          <Input
            id="instagram"
            name="instagram_handle"
            value={playerData.instagram_handle}
            onChange={handlePlayerChange}
            required
            placeholder="@handle or n/a"
            className="mt-1"
            data-testid="player-instagram-input"
          />
          <p className="text-xs text-muted-foreground mt-1">Used for tagging in photos. Enter "n/a" if none.</p>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="emergencyName">Emergency Contact *</Label>
            <Input
              id="emergencyName"
              name="emergency_contact_name"
              value={playerData.emergency_contact_name}
              onChange={handlePlayerChange}
              required
              placeholder="Contact name"
              className="mt-1"
              data-testid="player-emergency-name-input"
            />
          </div>
          
          <div>
            <Label htmlFor="emergencyPhone">Emergency Phone *</Label>
            <Input
              id="emergencyPhone"
              name="emergency_contact_phone"
              type="tel"
              value={playerData.emergency_contact_phone}
              onChange={handlePlayerChange}
              required
              placeholder="(555) 123-4567"
              className="mt-1"
              data-testid="player-emergency-phone-input"
            />
          </div>
        </div>
        
        <Button type="submit" disabled={loading} className="w-full btn-primary" data-testid="add-player-btn">
          {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
          Continue to Waiver
          <ArrowRight size={16} className="ml-2" />
        </Button>
      </form>
    </div>
  );
  
  // Step 3: Waiver
  const renderWaiverStep = () => (
    <div className="space-y-4">
      <div className="text-center">
        <h2 className="text-2xl font-heading text-white mb-2">Sign Waiver</h2>
        <p className="text-muted-foreground">Required for {createdPlayer?.name || 'your player'}</p>
      </div>
      
      {waiverStep === 1 && (
        <div className="space-y-4">
          <div 
            ref={waiverScrollRef}
            onScroll={handleScroll}
            className="bg-background/50 border border-border rounded-lg p-4 h-64 overflow-y-auto text-sm"
          >
            {waiverTemplate ? (
              <div className="space-y-4">
                <h3 className="font-bold text-lg">{waiverTemplate.title}</h3>
                <div dangerouslySetInnerHTML={{ __html: waiverTemplate.content?.replace(/\n/g, '<br/>') || '' }} />
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="font-bold">STAY ON HOOPS - WAIVER & RELEASE</h3>
                <p>By signing this waiver, you acknowledge and agree to the following terms...</p>
                <p>1. ASSUMPTION OF RISK: Basketball is a physical activity that carries inherent risks of injury.</p>
                <p>2. RELEASE OF LIABILITY: You release STAY ON Hoops from any claims arising from participation.</p>
                <p>3. MEDICAL AUTHORIZATION: You authorize emergency medical treatment if needed.</p>
                <p>4. PHOTO/VIDEO CONSENT: You consent to photos/videos being used for promotional purposes.</p>
                <p>5. RULES AGREEMENT: You agree to follow all program rules and guidelines.</p>
                <p className="text-muted-foreground mt-4">Scroll down to continue...</p>
              </div>
            )}
          </div>
          
          <Button 
            onClick={proceedToConsent} 
            disabled={!hasScrolledToBottom}
            className="w-full btn-primary"
            data-testid="waiver-continue-btn"
          >
            {hasScrolledToBottom ? 'I Have Read the Waiver' : 'Please Scroll to Read'}
            <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      )}
      
      {waiverStep === 2 && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Please confirm the following:</p>
          
          <div className="space-y-3">
            <label className="flex items-start gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-background/50">
              <Checkbox
                checked={isParentGuardian}
                onCheckedChange={setIsParentGuardian}
                data-testid="consent-parent-checkbox"
              />
              <span className="text-sm">I am the parent/legal guardian of {createdPlayer?.name}</span>
            </label>
            
            <label className="flex items-start gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-background/50">
              <Checkbox
                checked={consentDataCollection}
                onCheckedChange={setConsentDataCollection}
                data-testid="consent-data-checkbox"
              />
              <span className="text-sm">I consent to data collection for program purposes (COPPA compliant)</span>
            </label>
            
            <label className="flex items-start gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-background/50">
              <Checkbox
                checked={consentTermsPrivacy}
                onCheckedChange={setConsentTermsPrivacy}
                data-testid="consent-terms-checkbox"
              />
              <span className="text-sm">I agree to the Terms of Service and Privacy Policy</span>
            </label>
            
            <label className="flex items-start gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-background/50">
              <Checkbox
                checked={consentPhotoVideo}
                onCheckedChange={setConsentPhotoVideo}
                data-testid="consent-photo-checkbox"
              />
              <span className="text-sm">I consent to photos/videos of my child for promotional use</span>
            </label>
          </div>
          
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setWaiverStep(1)} className="flex-1">
              <ArrowLeft size={16} className="mr-2" />
              Back
            </Button>
            <Button 
              onClick={proceedToSign}
              disabled={!isParentGuardian || !consentDataCollection || !consentTermsPrivacy || !consentPhotoVideo}
              className="flex-1 btn-primary"
              data-testid="waiver-consent-continue-btn"
            >
              Continue
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        </div>
      )}
      
      {waiverStep === 3 && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Sign below to complete the waiver:</p>
          
          <div>
            <Label htmlFor="signedName">Your Full Legal Name *</Label>
            <Input
              id="signedName"
              value={signedName}
              onChange={(e) => setSignedName(e.target.value)}
              placeholder="Type your full name as signature"
              className="mt-1 text-lg font-signature"
              data-testid="waiver-signature-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Signing for: {createdPlayer?.name}
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setWaiverStep(2)} className="flex-1">
              <ArrowLeft size={16} className="mr-2" />
              Back
            </Button>
            <Button 
              onClick={handleSignWaiver}
              disabled={loading || !signedName.trim()}
              className="flex-1 btn-primary"
              data-testid="waiver-sign-btn"
            >
              {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
              Sign Waiver
              <CheckCircle size={16} className="ml-2" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
  
  // Step 4: Complete
  const renderCompleteStep = () => (
    <div className="space-y-6 text-center">
      <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
        <CheckCircle size={48} weight="fill" className="text-green-500" />
      </div>
      
      <div>
        <h2 className="text-2xl font-heading text-white mb-2">You're All Set!</h2>
        <p className="text-muted-foreground">
          {createdPlayer?.name} is ready to join a session
        </p>
      </div>
      
      <div className="bg-background/50 border border-border rounded-lg p-4 text-left">
        <h3 className="font-semibold mb-2">What's next?</h3>
        <ul className="text-sm text-muted-foreground space-y-2">
          <li className="flex items-center gap-2">
            <CheckCircle size={16} className="text-green-500" />
            Account created
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle size={16} className="text-green-500" />
            {createdPlayer?.name} added
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle size={16} className="text-green-500" />
            Waiver signed
          </li>
          <li className="flex items-center gap-2">
            <Basketball size={16} className="text-primary" />
            Browse sessions and register!
          </li>
        </ul>
      </div>
      
      <Button onClick={goToSessions} className="w-full btn-primary text-lg py-6" data-testid="find-session-btn">
        <Basketball size={24} className="mr-2" />
        Find a Session
      </Button>
    </div>
  );
  
  return (
    <div className="min-h-screen bg-background px-4 py-8">
      <div className="max-w-md mx-auto">
        {/* Logo */}
        <div className="text-center mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_6652d962-fcde-40fe-ae1a-478daa9a079c/artifacts/xf9z325b_IMG_9879.png" 
            alt="STAY ON Logo" 
            className="h-12 mx-auto"
          />
        </div>
        
        {/* Progress */}
        {renderProgress()}
        
        {/* Card */}
        <div className="bg-card border border-border rounded-xl p-6 shadow-lg">
          {currentStep === 1 && renderAccountStep()}
          {currentStep === 2 && renderPlayerStep()}
          {currentStep === 3 && renderWaiverStep()}
          {currentStep === 4 && renderCompleteStep()}
        </div>
      </div>
    </div>
  );
}
